import { LngLatLike } from 'mapbox-gl';
import { createSelector } from 'reselect';

import { State } from '../../../app/store/state.types.ts';

import {
  ItemEditState,
  LastSentEditMetadata,
  SetLastSentEditMetadataPayload,
} from './item-edit.types.ts';

export interface EditableMapFeature {
  key: string;
  type: EDITABLE_FEATURE_TYPES;
  geometry: LngLatLike;
  active: boolean;
  changed: boolean;
}

export enum EDITABLE_FEATURE_TYPES {
  itemMarker = 'itemMarker',
  routablePoint = 'routablePoint',
}

const initialState: ItemEditState = {
  location: null,
  dragged: false,

  longPressLocation: null,
  formState: null,
  currentFormId: null,
  markerPositionCountryCode: null,
  lastSentEditMetadata: null,
  editableMapFeatures: [],
};

const Actions = {
  MARKER_DRAGGED: 'item-edit/marker-dragged',
  MARKER_INITIALIZED: 'item-edit/marker-initialized',
  MARKER_REMOVED: 'item-edit/marker-removed',
  MARKER_POSITION_COUNTRY_CODE_CHANGED:
    'item-edit/marker-position-country-code-changed',
  INITIALIZE_ROUTABLE_POINTS: 'item-edit/initialize-routable-points',
  CLEAR_ROUTABLE_POINTS: 'item-edit/clear-routable-points',
  ROUTABLE_POINT_ADDED: 'item-edit/routable-point-added',
  ROUTABLE_POINT_REMOVED: 'item-edit/routable-point-removed',
  SET_ROUTABLE_POINT_ACTIVE: 'item-edit/set-routable-point-active',
  ROUTABLE_POINT_LOCATION_CHANGED: 'item-edit/routable-point-location-changed',
  LONG_PRESS_LOCATION_CHANGED: 'item-edit/long-press-location-changed',
  FORM_STATE_CHANGED: 'item-edit/form-state-changed',
  CURRENT_FORM_ID_CHANGED: 'item-edit/current-form-id-changed',
  CLEAR_FORM_STATE: 'item-edit/clear-form-state',
  SET_LAST_SENT_EDIT_METADATA: 'item-edit/set-last-edit-sent-metadata',
  CLEAR_LAST_SENT_EDIT_METADATA: 'item-edit/clear-last-edit-sent-metadata',
};

const getRoutablePointFeatureKey = (idx) => `routablePoint_${idx}`;
const getRoutablePointIndexFromKey = (key) =>
  Number(key.replace('routablePoint_', ''));

const markerDragged = (location) => ({
  type: Actions.MARKER_DRAGGED,
  location,
});

const markerInitialized = ({ location, dragged = false }) => ({
  type: Actions.MARKER_INITIALIZED,
  location,
  dragged,
});

const markerRemoved = () => ({
  type: Actions.MARKER_REMOVED,
});

const markerPositionCountryCodeChanged = (markerPositionCountryCode) => ({
  type: Actions.MARKER_POSITION_COUNTRY_CODE_CHANGED,
  markerPositionCountryCode,
});

const longPressLocationChanged = (longPressLocation) => ({
  type: Actions.LONG_PRESS_LOCATION_CHANGED,
  longPressLocation,
});

const initializeRoutablePoints = (routablePoints = []) => ({
  type: Actions.INITIALIZE_ROUTABLE_POINTS,
  routablePoints,
});

const clearRoutablePoints = () => ({
  type: Actions.CLEAR_ROUTABLE_POINTS,
});

const routablePointAdded = ({
  location,
  active = false,
}: { location?; active?: boolean } = {}) => ({
  type: Actions.ROUTABLE_POINT_ADDED,
  payload: { location, active },
});

const routablePointRemoved = (index) => ({
  type: Actions.ROUTABLE_POINT_REMOVED,
  index,
});

const setRoutablePointActive = (index = -1) => ({
  type: Actions.SET_ROUTABLE_POINT_ACTIVE,
  index,
});

const routablePointLocationChanged = (index, location) => ({
  type: Actions.ROUTABLE_POINT_LOCATION_CHANGED,
  payload: { index, location },
});

const formStateChanged = (formState) => ({
  type: Actions.FORM_STATE_CHANGED,
  formState,
});

const currentFormIdChanged = (formId) => ({
  type: Actions.CURRENT_FORM_ID_CHANGED,
  formId,
});

const clearFormState = () => ({
  type: Actions.CLEAR_FORM_STATE,
});

const setLastSentEditMetadata = (payload: SetLastSentEditMetadataPayload) => ({
  type: Actions.SET_LAST_SENT_EDIT_METADATA,
  payload,
});

const clearLastSentEditMetadata = () => ({
  type: Actions.CLEAR_LAST_SENT_EDIT_METADATA,
});

function reducer(state = initialState, action): ItemEditState {
  switch (action.type) {
    case Actions.MARKER_DRAGGED:
      return {
        ...state,
        editableMapFeatures: [
          ...state.editableMapFeatures.filter(
            (feature: EditableMapFeature) =>
              feature.type !== EDITABLE_FEATURE_TYPES.itemMarker,
          ),
          {
            key: 'itemEditMarker',
            type: EDITABLE_FEATURE_TYPES.itemMarker,
            geometry: action.location,
            changed: true,
          },
        ],
      };
    case Actions.MARKER_INITIALIZED:
      return {
        ...state,
        editableMapFeatures: [
          ...state.editableMapFeatures.filter(
            (feature: EditableMapFeature) =>
              feature.type !== EDITABLE_FEATURE_TYPES.itemMarker,
          ),
          {
            key: 'itemEditMarker',
            type: EDITABLE_FEATURE_TYPES.itemMarker,
            geometry: action.location,
            changed: action.dragged || false,
          },
        ],
      };
    case Actions.MARKER_REMOVED:
      return {
        ...state,
        editableMapFeatures: state.editableMapFeatures.filter(
          (feature: EditableMapFeature) =>
            feature.type !== EDITABLE_FEATURE_TYPES.itemMarker,
        ),
      };
    case Actions.MARKER_POSITION_COUNTRY_CODE_CHANGED:
      return {
        ...state,
        markerPositionCountryCode: action.markerPositionCountryCode,
      };
    case Actions.LONG_PRESS_LOCATION_CHANGED:
      return {
        ...state,
        longPressLocation: action.longPressLocation,
      };
    case Actions.INITIALIZE_ROUTABLE_POINTS:
      return {
        ...state,
        editableMapFeatures: [
          ...state.editableMapFeatures.filter(
            (feature: EditableMapFeature) =>
              feature.type !== EDITABLE_FEATURE_TYPES.routablePoint,
          ),
          ...action.routablePoints.map((point, idx) => ({
            key: getRoutablePointFeatureKey(idx),
            type: EDITABLE_FEATURE_TYPES.routablePoint,
            geometry: point.location,
            active: point.active,
            changed: point.changed || false,
          })),
        ],
      };
    case Actions.CLEAR_ROUTABLE_POINTS:
      return {
        ...state,
        editableMapFeatures: state.editableMapFeatures.filter(
          (feature: EditableMapFeature) =>
            feature.type !== EDITABLE_FEATURE_TYPES.routablePoint,
        ),
      };
    case Actions.ROUTABLE_POINT_ADDED: {
      const maxRoutablePointIndex = Math.max(
        ...state.editableMapFeatures
          .filter(
            (feature: EditableMapFeature) =>
              feature.type === EDITABLE_FEATURE_TYPES.routablePoint,
          )
          .map((feature: EditableMapFeature) =>
            getRoutablePointIndexFromKey(feature.key),
          ),
        -1,
      );

      return {
        ...state,
        editableMapFeatures: [
          ...state.editableMapFeatures,
          {
            key: getRoutablePointFeatureKey(maxRoutablePointIndex + 1),
            type: EDITABLE_FEATURE_TYPES.routablePoint,
            geometry: action.payload.location || null,
            active: action.payload.active || false,
            changed: false,
          },
        ],
      };
    }
    case Actions.ROUTABLE_POINT_REMOVED:
      return {
        ...state,
        editableMapFeatures: state.editableMapFeatures
          .filter(
            (feature: EditableMapFeature) =>
              feature.key !== getRoutablePointFeatureKey(action.index),
          )
          .map((feature: EditableMapFeature) => {
            if (feature.type !== EDITABLE_FEATURE_TYPES.routablePoint) {
              return feature;
            }

            const featureIndex = getRoutablePointIndexFromKey(feature.key);

            if (featureIndex > action.index) {
              return {
                ...feature,
                key: getRoutablePointFeatureKey(featureIndex - 1),
              };
            }

            return feature;
          }),
      };
    case Actions.SET_ROUTABLE_POINT_ACTIVE: {
      const actionFeature = state.editableMapFeatures.find(
        (feature: EditableMapFeature) =>
          feature.key === getRoutablePointFeatureKey(action.index),
      ) as { active: boolean } | undefined;

      if (actionFeature?.active) {
        return state;
      }

      return {
        ...state,
        editableMapFeatures: state.editableMapFeatures.map(
          (feature: EditableMapFeature) => {
            if (feature.type !== EDITABLE_FEATURE_TYPES.routablePoint) {
              return feature;
            }

            return {
              ...feature,
              active: feature === actionFeature,
            };
          },
        ),
      };
    }
    case Actions.ROUTABLE_POINT_LOCATION_CHANGED: {
      return {
        ...state,
        editableMapFeatures: state.editableMapFeatures.map(
          (feature: EditableMapFeature) => {
            if (
              feature.type !== EDITABLE_FEATURE_TYPES.routablePoint ||
              feature.key !== getRoutablePointFeatureKey(action.payload.index)
            ) {
              return feature;
            }

            return {
              ...feature,
              geometry: action.payload.location,
              changed: true,
            };
          },
        ),
      };
    }
    case Actions.FORM_STATE_CHANGED:
      return {
        ...state,
        formState: action.formState,
      };
    case Actions.CURRENT_FORM_ID_CHANGED:
      return {
        ...state,
        currentFormId: action.formId,
      };
    case Actions.CLEAR_FORM_STATE:
      return {
        ...state,
        formState: null,
        currentFormId: null,
      };
    case Actions.SET_LAST_SENT_EDIT_METADATA:
      return {
        ...state,
        lastSentEditMetadata: action.payload,
      };
    case Actions.CLEAR_LAST_SENT_EDIT_METADATA:
      return {
        ...state,
        lastSentEditMetadata: null,
      };
  }
  return state;
}

function selectItemEditState(state: State) {
  return state.itemEdit;
}

const selectLocation = createSelector(selectItemEditState, (itemEdit) => {
  return itemEdit.editableMapFeatures.find(
    (feature) => feature.type === EDITABLE_FEATURE_TYPES.itemMarker,
  )?.geometry;
});

const selectMarkerPositionCountryCode = createSelector(
  selectItemEditState,
  (itemEdit) => itemEdit.markerPositionCountryCode,
);

const selectLongPressLocation = createSelector(
  selectItemEditState,
  (itemEdit) => itemEdit.longPressLocation,
);

const selectDraggedState = createSelector(selectItemEditState, (itemEdit) => {
  return itemEdit.editableMapFeatures.find(
    (feature) => feature.type === EDITABLE_FEATURE_TYPES.itemMarker,
  )?.changed;
});

const selectEditableMapFeatures = createSelector(
  selectItemEditState,
  (itemEdit) => itemEdit.editableMapFeatures,
);

const selectRoutablePoints = createSelector(
  selectEditableMapFeatures,
  (editableMapFeatures) =>
    editableMapFeatures
      .filter(
        (feature) => feature.type === EDITABLE_FEATURE_TYPES.routablePoint,
      )
      .map((feature) => ({
        location: feature.geometry,
        active: feature.active,
        moved: feature.changed,
      })),
);

const selectFormState = createSelector(
  selectItemEditState,
  (itemEdit) => itemEdit.formState,
);

const selectCurrentFormId = createSelector(
  selectItemEditState,
  (itemEdit) => itemEdit.currentFormId,
);

const selectLastSentEditMetadata = createSelector(
  selectItemEditState,
  (itemEdit): LastSentEditMetadata | null => itemEdit.lastSentEditMetadata,
);

export {
  reducer,
  // actions
  markerDragged,
  markerInitialized,
  markerRemoved,
  markerPositionCountryCodeChanged,
  longPressLocationChanged,
  initializeRoutablePoints,
  clearRoutablePoints,
  routablePointAdded,
  routablePointRemoved,
  setRoutablePointActive,
  routablePointLocationChanged,
  formStateChanged,
  currentFormIdChanged,
  clearFormState,
  setLastSentEditMetadata,
  clearLastSentEditMetadata,
  // selectors
  selectLocation,
  selectMarkerPositionCountryCode,
  selectLongPressLocation,
  selectDraggedState,
  selectEditableMapFeatures,
  selectRoutablePoints,
  selectFormState,
  selectCurrentFormId,
  selectLastSentEditMetadata,
};
